/**
 * Excel/CSV-Übernahme für Rechnungen (pure functions, ohne React):
 * Tab/;/, -Erkennung, quote-bewusstes Parsen, automatische Spaltenzuordnung
 * (deutsche Überschriften, Umlaut-insensitiv) und de-DE Währungs-/Datums-
 * Parsing. Client-Validierung spiegelt validateAndNormalize (Brutto > 0,
 * ε 0.02, Fälligkeit ≥ Rechnungsdatum) — der Server revalidiert ohnehin.
 */

export type InvoiceImportField =
  | 'invoice_number' | 'invoice_date' | 'due_date' | 'contact' | 'bauvorhaben'
  | 'gewerk' | 'description' | 'amount_net' | 'amount_tax' | 'tax_rate_percent'
  | 'amount_gross' | 'direction';

/** Eine geparste, zugeordnete Import-Zeile (vor dem Server-Roundtrip). */
export interface ParsedInvoiceRow {
  /** 1-basierte Excel-Zeile (inkl. Header), fürs Error-Reporting. */
  rowNumber: number;
  /** Rohwerte je Feld (vor der Normalisierung), für die Vorschau. */
  raw: Record<InvoiceImportField, string>;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  /** Lieferanten-/Kundenname (Zuordnung zum Kontakt passiert im Modal). */
  contact_name: string | null;
  /** BV-Name/Nummer (Zuordnung gegen die geladene Liste im Modal). */
  bauvorhaben_ref: string | null;
  gewerk_ref: string | null;
  description: string | null;
  amount_net: number | null;
  amount_tax: number | null;
  tax_rate_percent: number | null;
  amount_gross: number | null;
  direction: 'eingang' | 'ausgang';
  /** Ausgeschlossene Zeilen (Checkbox) werden nicht importiert. */
  selected: boolean;
  /** Zuordnungen — serverseitig ermittelt (Kontakt-Match) bzw. per
   *  Client-Match gegen die geladenen BV-/Gewerk-Listen, im Modal änderbar. */
  contact_id: number | null;
  bauvorhaben_id: number | null;
  gewerk_id: number | null;
  /** Zeilenfehler (Blocker) und Warnungen (importiert trotzdem). */
  errors: string[];
  warnings: string[];
}

export interface ParseResult {
  headers: string[];
  /** mapping: Feld → Spaltenindex (-1 = nicht zugeordnet). */
  mapping: Record<InvoiceImportField, number>;
  rows: ParsedInvoiceRow[];
}

// ---------- Delimiter & Parsen ----------

/** TSV (Excel-Copy) bevorzugt, dann Semikolon (deutsche CSV), dann Komma. */
export function detectDelimiter(text: string): string {
  const sample = text.split(/\r?\n/).slice(0, 5).join('\n');
  const counts: Array<[string, number]> = [
    ['\t', (sample.match(/\t/g) || []).length],
    [';', (sample.match(/;/g) || []).length],
    [',', (sample.match(/,/g) || []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : '\t';
}

/** Quote-bewusstes Splitten (", "", \"). */
export function splitDelimitedLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((c) => c.trim());
}

// ---------- de-DE Werte ----------

/** "1.234,56 €" → 1234.56; "1,234.56" (EN) → 1234.56; akzeptiert auch "1234,56". */
export function parseDeAmount(raw: string): number | null {
  let text = raw.replace(/[€$%\s]/g, '').replace(/−/g, '-');
  if (text === '' || text === '-') return null;
  // Punkt = Tausender, Komma = Dezimal (de) — wenn beide vorkommen, eindeutig.
  if (text.includes(',') && text.includes('.')) {
    if (text.lastIndexOf(',') > text.lastIndexOf('.')) {
      text = text.replace(/\./g, '').replace(',', '.');
    } else {
      text = text.replace(/,/g, '');
    }
  } else if (text.includes(',')) {
    // Komma: Dezimaltrenner, wenn dahinter genau 1-2 Ziffern am Ende stehen,
    // sonst Tausender ("1,234" im EN-Stil ohne Punkt ist zweideutig — de lesen).
    const parts = text.split(',');
    const decimals = parts[parts.length - 1];
    text = decimals.length === 1 || decimals.length === 2 ? text.replace(',', '.') : text.replace(/,/g, '');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(text)) {
    // "1.234" ohne Nachkommastellen: Tausender-Punkt (de) lesen.
    text = text.replace(/\./g, '');
  }
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** dd.MM.yyyy / dd,MM,yyyy / yyyy-MM-dd → ISO (yyyy-MM-dd), sonst null. */
export function parseDeDate(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }
  const german = text.match(/^(\d{1,2})[.,/-](\d{1,2})[.,/-](\d{2,4})/);
  if (german) {
    const day = german[1].padStart(2, '0');
    const month = german[2].padStart(2, '0');
    let year = german[3];
    if (year.length === 2) year = Number(year) >= 70 ? `19${year}` : `20${year}`;
    // Plausibilität (Monat 1-12, Tag 1-31) — echte Gültigkeit prüft der Server.
    const monthNum = Number(month);
    const dayNum = Number(day);
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
    return `${year}-${month}-${day}`;
  }
  return null;
}

// ---------- Header-Zuordnung ----------

const normalizeHeader = (header: string): string =>
  header
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9%/ ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Synonym-Liste je Feld; Treffer = erstes Pattern, das im Header enthalten ist. */
const HEADER_SYNONYMS: Array<[InvoiceImportField, string[]]> = [
  ['invoice_number', ['rechnungsnummer', 'rechnungsnr', 'rechnungs-nr', 'rechnung nr', 'rechnungs nummer', 'nr rechnung', 'invoice number', 'rechnungsnr.']],
  ['invoice_date', ['rechnungsdatum', 'rechnungs datum', 'datum rechnung', 'belegdatum', 'beleg datum', 'invoice date']],
  ['due_date', ['faellig', 'fällig', 'faelligkeitsdatum', 'fälligkeitsdatum', 'faellig am', 'due date', 'zahlbar bis']],
  ['contact', ['lieferant', 'kunde', 'empfaenger', 'empfänger', 'lieferant/kunde', 'kontakt', 'firma', 'supplier', 'customer']],
  ['bauvorhaben', ['bauvorhaben', 'projekt', 'bv', 'baustelle', 'object', 'objekt']],
  ['gewerk', ['gewerk', 'gewerke', 'trade']],
  ['description', ['betreff', 'beschreibung', 'kommentar', 'verwendung', 'text', 'subject']],
  ['tax_rate_percent', ['mwst-satz', 'mwstsatz', 'mwst satz', 'mwst %', 'mwst %', 'ust-satz', 'steuersatz', 'steuer-satz', 'vat rate', 'satz']],
  ['amount_net', ['nettobetrag', 'netto betrag', 'netto', 'nettobetrag (eur)', 'net']],
  ['amount_tax', ['mwst-betrag', 'mwstbetrag', 'mwst betrag', 'mwst', 'ust-betrag', 'ustbetrag', 'steuerbetrag', 'steuer', 'vat', 'tax']],
  ['amount_gross', ['bruttobetrag', 'brutto betrag', 'brutto', 'gesamtbetrag', 'gesamt betrag', 'gesamt', 'summe', 'betrag', 'betrag (eur)', 'gross', 'total']],
  ['direction', ['richtung', 'art', 'typ', 'e/r']],
];

/**
 * Automatische Zuordnung: Reihenfolge der Synonym-Liste ist die
 * Priorität ("mwst-satz" vor "mwst", "brutto" vor generischem "betrag").
 * Jede Spalte wird höchstens einmal vergeben.
 */
export function autoMapHeaders(headers: string[]): Record<InvoiceImportField, number> {
  const mapping = Object.fromEntries(
    HEADER_SYNONYMS.map(([field]) => [field, -1])
  ) as Record<InvoiceImportField, number>;
  const normalized = headers.map(normalizeHeader);
  const taken = new Set<number>();

  // Satz-Feld zuerst disambiguieren: Header mit "satz" oder "%" gewinnt.
  const rateCandidates = normalized
    .map((h, index) => ({ h, index }))
    .filter(({ h }) => h.includes('satz') || h.includes('%'));
  for (const { h, index } of rateCandidates) {
    const synonyms = HEADER_SYNONYMS.find(([field]) => field === 'tax_rate_percent')?.[1] ?? [];
    if (synonyms.some((s) => normalizeHeader(s) === h) || h.includes('mwst') || h.includes('ust') || h.includes('steuer')) {
      mapping.tax_rate_percent = index;
      taken.add(index);
      break;
    }
  }

  for (const [field, synonyms] of HEADER_SYNONYMS) {
    if (mapping[field] !== -1) continue;
    for (let index = 0; index < normalized.length; index++) {
      if (taken.has(index)) continue;
      const header = normalized[index];
      if (synonyms.some((s) => normalizeHeader(s) === header)) {
        mapping[field] = index;
        taken.add(index);
        break;
      }
    }
    if (mapping[field] !== -1) continue;
    // Lockerer Fallback: Synonym als Teilstring im Header.
    for (let index = 0; index < normalized.length; index++) {
      if (taken.has(index)) continue;
      const header = normalized[index];
      if (synonyms.some((s) => normalizeHeader(s) && header.includes(normalizeHeader(s)))) {
        mapping[field] = index;
        taken.add(index);
        break;
      }
    }
  }
  return mapping;
}

// ---------- Zeilen parsen ----------

/**
 * Geparste Tabelle → ParsedInvoiceRow[]: Zahlen/Daten de-kodiert,
 * Client-Validierung wie der Server (Blocker = nicht importierbar),
 * Warnungen sind Hinweise (z. B. doppelte Nummer innerhalb der Liste).
 */
export function parseInvoiceRows(
  _headers: string[],
  mapping: Record<InvoiceImportField, number>,
  dataRows: string[][],
  startIndex = 2
): ParsedInvoiceRow[] {
  const cell = (row: string[], field: InvoiceImportField): string => {
    const index = mapping[field];
    return index >= 0 ? (row[index] ?? '').trim() : '';
  };

  const parsed = dataRows.map((row, i) => {
    const rowNumber = startIndex + i;
    const gross = cell(row, 'amount_gross') ? parseDeAmount(cell(row, 'amount_gross')) : null;
    const net = cell(row, 'amount_net') ? parseDeAmount(cell(row, 'amount_net')) : null;
    const tax = cell(row, 'amount_tax') ? parseDeAmount(cell(row, 'amount_tax')) : null;
    const rate = cell(row, 'tax_rate_percent') ? parseDeAmount(cell(row, 'tax_rate_percent')) : null;
    const invoiceDate = cell(row, 'invoice_date') ? parseDeDate(cell(row, 'invoice_date')) : null;
    const dueDate = cell(row, 'due_date') ? parseDeDate(cell(row, 'due_date')) : null;
    const directionRaw = cell(row, 'direction').toLowerCase();
    // Leere Spalte → eingang (Default); "R"/"Ausgang"/"Verkauf" → ausgang.
    const direction: 'eingang' | 'ausgang' =
      /ausgang|verkauf|outgoing|sales|^r$/i.test(directionRaw) ? 'ausgang' : 'eingang';

    const errors: string[] = [];
    const warnings: string[] = [];

    const invoiceNumber = cell(row, 'invoice_number');
    if (!invoiceNumber) errors.push('Rechnungsnummer fehlt');
    if (!invoiceDate) errors.push('Rechnungsdatum fehlt oder unlesbar (erwartet TT.MM.JJJJ)');
    if (gross == null) errors.push('Bruttobetrag fehlt oder unlesbar');
    else if (gross <= 0) errors.push('Bruttobetrag muss größer als 0 sein');

    if (net != null && tax != null && gross != null && Math.abs(net + tax - gross) > 0.02) {
      errors.push('Netto + MwSt ergibt nicht das Brutto');
    }
    if (invoiceDate && dueDate && dueDate < invoiceDate) {
      errors.push('Fälligkeit liegt vor dem Rechnungsdatum');
    }
    // Konsistenz-Hinweise (importiert trotzdem — Server/Satz ableiten):
    if (net == null && tax == null && gross != null) {
      warnings.push('Kein Netto/MwSt angegeben — wird beim Satz 19 % hergeleitet?');
    }

    return {
      rowNumber,
      raw: {
        invoice_number: invoiceNumber,
        invoice_date: cell(row, 'invoice_date'),
        due_date: cell(row, 'due_date'),
        contact: cell(row, 'contact'),
        bauvorhaben: cell(row, 'bauvorhaben'),
        gewerk: cell(row, 'gewerk'),
        description: cell(row, 'description'),
        amount_net: cell(row, 'amount_net'),
        amount_tax: cell(row, 'amount_tax'),
        tax_rate_percent: cell(row, 'tax_rate_percent'),
        amount_gross: cell(row, 'amount_gross'),
        direction: directionRaw,
      },
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate,
      due_date: dueDate,
      contact_name: cell(row, 'contact') || null,
      bauvorhaben_ref: cell(row, 'bauvorhaben') || null,
      gewerk_ref: cell(row, 'gewerk') || null,
      description: cell(row, 'description') || null,
      amount_net: net,
      amount_tax: tax,
      tax_rate_percent: rate,
      amount_gross: gross,
      direction,
      selected: true,
      contact_id: null,
      bauvorhaben_id: null,
      gewerk_id: null,
      errors,
      warnings,
    } as ParsedInvoiceRow;
  });

  // Doppelte Nummern innerhalb der Importliste (Batch-Dedupe serverseitig).
  const seen = new Map<string, number>();
  for (const row of parsed) {
    if (!row.invoice_number) continue;
    const first = seen.get(row.invoice_number);
    if (first != null) {
      row.errors.push(`Doppelt in der Importliste (Zeile ${first})`);
    } else {
      seen.set(row.invoice_number, row.rowNumber);
    }
  }
  return parsed;
}

/** Tabelle komplett parsen: Delimiter-Erkennung + Header-Zuordnung + Zeilen. */
export function parseInvoiceTable(text: string, delimiterOverride?: string): ParseResult {
  const delimiter = delimiterOverride ?? detectDelimiter(text);
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) {
    return { headers: [], mapping: emptyMapping(), rows: [] };
  }
  const headers = splitDelimitedLine(lines[0], delimiter);
  const mapping = autoMapHeaders(headers);
  const rows = parseInvoiceRows(
    headers,
    mapping,
    lines.slice(1).map((line) => splitDelimitedLine(line, delimiter))
  );
  return { headers, mapping, rows };
}

function emptyMapping(): Record<InvoiceImportField, number> {
  return Object.fromEntries(
    ([
      'invoice_number', 'invoice_date', 'due_date', 'contact', 'bauvorhaben',
      'gewerk', 'description', 'amount_net', 'amount_tax', 'tax_rate_percent',
      'amount_gross', 'direction',
    ] as InvoiceImportField[]).map((field) => [field, -1])
  ) as Record<InvoiceImportField, number>;
}